import React from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory, Link } from 'react-router';
import AuthMixin from '../../mixins/authenticationMixin';
import LoginStore from '../../stores/login-store';
import Analytics from '../../services/analytics';
import { getData, postData, deleteData } from '../../api/fetchData';
import ApiEndPoints from '../../constants/endpoints';
import { Remarkable } from 'remarkable';
import AxonIcon from '../../components/icons/axon-icon';
import logo from '../../../images/logo.svg';

import './chat-page.scss';

const API_ROOT = ApiEndPoints.CONVERSATIONS;

const fileNameToSlug = (fileName) => {
    return fileName
        .toLowerCase()
        .replace(/^\w+\d*\.\s*/, '') // Remove prefixes like "B1. "
        .replace(/\s+/g, '-')        // Replace spaces with -
        .replace(/[^\w-]/g, '')     // Remove non-word chars
        .replace(/--+/g, '-')       // Replace multiple - with single -
        .trim();
};

class ChatPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            chatValue: '',
            hasMessages: false,
            chatActive: false,
            isTyping: false,
            messages: [],
            references: [],
            conversations: [],
            currentConversationId: null,
            loading: true,
            error: null,
            sidebarOpen: false,
            sourcesOpen: false
        };

        // messages are rendered with dangerouslySetInnerHTML, so HTML in them must be escaped, not run
        this.md = new Remarkable({ breaks: true, html: false });

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleKey = this.handleKey.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.toggleSidebar = this.toggleSidebar.bind(this);
        this.toggleSources = this.toggleSources.bind(this);
        this.closePanels = this.closePanels.bind(this);
    }

    componentDidMount() {
        this.loadConversations();
        const { conversationId } = this.props.params;

        // Check for query parameter from Axon search
        const query = new URLSearchParams(this.props.location.search);
        const queryParam = query.get('q');

        if (queryParam) {
            // Pre-populate and auto-send the query from Axon search
            this.setState({
                chatValue: queryParam,
                loading: false
            }, () => {
                // Strip the ?q= param so a refresh doesn't re-fire the message
                browserHistory.replace(this.props.location.pathname);
                this.sendMessage();
            });
        } else if (this.props.location && this.props.location.state && this.props.location.state.prePopulatedMessage) {
            // Check if there's a pre-populated message from navigation state
            this.setState({
                chatValue: this.props.location.state.prePopulatedMessage,
                loading: false
            }, () => {
                // Focus the textarea after state update
                const textarea = document.getElementById('chatInput');
                if (textarea) {
                    textarea.focus();
                    // Auto-resize for the pre-populated text
                    textarea.style.height = 'auto';
                    textarea.style.height = (textarea.scrollHeight) + 'px';
                }
            });
            // Clear the state after using it
            if (this.props.location.state) {
                delete this.props.location.state.prePopulatedMessage;
            }
        } else if (conversationId) {
            this.loadChat(conversationId);
        } else {
            this.setState({ loading: false });
        }
    }

    componentDidUpdate(prevProps) {
        const { conversationId: oldId } = prevProps.params;
        const { conversationId: newId } = this.props.params;
        if (newId !== oldId) {
            if (newId) {
                this.loadChat(newId);
            } else {
                this.setState({
                    messages: [],
                    references: [],
                    currentConversationId: null,
                    hasMessages: false,
                    chatActive: false
                });
            }
        }
    }

    loadConversations() {
        getData(API_ROOT, this.props.userApiRequest, {
            onSuccess: ({ data }) => {
                const conversations = data.conversations || (Array.isArray(data) ? data : []);
                const { conversationId } = this.props.params;
                this.setState({
                    conversations: conversations.map(c => ({ ...c, active: c.id === conversationId })),
                });
            },
            onError: ({ error }) => {
                console.error('Error loading conversations:', error);
                this.setState({ error: 'Failed to load conversations', loading: false });
            }
        });
    }

    loadChat(conversationId) {
        this.setState({ loading: true, error: null });
        getData(`${API_ROOT}/${conversationId}`, this.props.userApiRequest, {
            onSuccess: ({ data }) => {
                const messages = data.messages || [];

                // Extract unique references from all Axon messages
                let allReferences = [];
                const seenFiles = new Set();

                messages.forEach(msg => {
                    if (msg.role === 'axon' && msg.message_references && msg.message_references.length > 0) {
                        msg.message_references.forEach(ref => {
                            if (!seenFiles.has(ref.file_name)) {
                                allReferences.push(ref);
                                seenFiles.add(ref.file_name);
                            }
                        });
                    }
                });

                this.setState({
                    messages,
                    references: allReferences,
                    currentConversationId: conversationId,
                    hasMessages: messages.length > 0,
                    chatActive: messages.length > 0,
                    loading: false,
                    conversations: this.state.conversations.map(c => ({ ...c, active: c.id === conversationId })),
                }, () => this.scrollToConversationStart());
            },
            onError: ({ error }) => {
                console.error('Error loading chat:', error);
                this.setState({ error: 'Failed to load messages', loading: false });
            }
        });
    }

    handleInputChange(e) {
        this.setState({ chatValue: e.target.value });
        // Auto-resize textarea
        e.target.style.height = 'auto';
        e.target.style.height = (e.target.scrollHeight) + 'px';
    }

    handleKey(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.sendMessage();
        }
    }

    sendMessage() {
        const { chatValue, messages, currentConversationId } = this.state;
        const text = chatValue.trim();
        if (!text) return;

        const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const userMsg = { role: 'user', content: text, time };

        this.setState({
            messages: [...messages, userMsg],
            chatValue: '',
            hasMessages: true,
            chatActive: true,
            isTyping: true
        }, () => {
            this.scrollBottom();
            // Reset textarea height
            const textarea = document.getElementById('chatInput');
            if (textarea) textarea.style.height = 'auto';

            // If new conversation, create it first or use existing
            const conversationId = currentConversationId || 'new';
            Analytics.event('axon_question', { new_conversation: !currentConversationId });

            postData(`${API_ROOT}/${conversationId}/messages`, this.props.userApiRequest, {
                message: { role: 'user', content: text }
            }, {
                onSuccess: ({ data }) => {
                    const axonMsg = data.axon_message || data.message; // Support both formats
                    const userMsgServer = data.user_message || userMsg; // Fallback to local if server doesn't return it

                    // Capture the new conversation ID from response (id is now returned by backend fix)
                    let newConvId = data.id || data.conversation_id || (axonMsg && axonMsg.conversation_id) || null;
                    if (newConvId && typeof newConvId === 'object') {
                        newConvId = newConvId.$oid || newConvId.id || String(newConvId);
                    }

                    this.setState(prevState => {
                        // Replace the last temporary user message with the server-verified one
                        const updatedMessages = [...prevState.messages];
                        if (updatedMessages.length > 0 && updatedMessages[updatedMessages.length - 1].role === 'user') {
                            updatedMessages[updatedMessages.length - 1] = userMsgServer;
                        }

                        const newRefs = (axonMsg && axonMsg.message_references) || [];
                        const currentRefs = [...prevState.references];
                        const seenFiles = new Set(currentRefs.map(r => r.file_name));

                        newRefs.forEach(ref => {
                            if (!seenFiles.has(ref.file_name)) {
                                currentRefs.push(ref);
                                seenFiles.add(ref.file_name);
                            }
                        });

                        return {
                            messages: [...updatedMessages, axonMsg],
                            references: currentRefs,
                            isTyping: false,
                            currentConversationId: newConvId || prevState.currentConversationId
                        };
                    }, () => {
                        this.scrollToLatestAnswer();
                        if (conversationId === 'new' && newConvId) {
                            this.loadConversations();
                            // Update URL without reload — only if we have a real ID
                            browserHistory.push(`/chat/${newConvId}`);
                        }
                    });
                },
                onError: ({ response, error }) => {
                    if (response && response.statusCode === 403 && error && error.code === 'free_trial_expired') {
                        this.handleTrialExpired(text);
                        return;
                    }
                    console.error('Error saving message:', error);
                    this.setState({
                        isTyping: false,
                        error: 'Failed to send message. Please try again.'
                    });
                }
            });
        });
    }

    // The session's trial flag is only set at login, so the API can know the trial
    // has ended before the client does. Sync the flag and show the upgrade modal.
    handleTrialExpired(text) {
        LoginStore.updateSessionUserData({ free_trial_expired: true });
        this.setState(prevState => {
            const messages = prevState.messages.slice(0, -1);
            return {
                messages,
                chatValue: text,
                isTyping: false,
                hasMessages: messages.length > 0,
                chatActive: messages.length > 0,
                error: 'Your free trial has expired. Please upgrade to continue using Axon.'
            };
        });
        browserHistory.replace(`${window.location.pathname}?modal=upgrade`);
    }

    deleteConversation(id, e) {
        e.stopPropagation();
        if (!confirm('Are you sure you want to delete this conversation?')) return;

        deleteData(`${API_ROOT}/${id}`, this.props.userApiRequest, {
            onSuccess: () => {
                this.setState({
                    conversations: this.state.conversations.filter(c => c.id !== id)
                });
                if (this.state.currentConversationId === id) {
                    browserHistory.push('/chat');
                }
            }
        });
    }

    scrollBottom() {
        if (this.messagesEnd) {
            this.messagesEnd.scrollIntoView({ behavior: 'smooth' });
        }
    }

    scrollToConversationStart() {
        // Used after loading a previous conversation — show the first question, not the latest answer.
        const firstUserMsg = document.querySelector('.chat-messages .msg-user');
        if (firstUserMsg) {
            firstUserMsg.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
    }

    scrollToLatestAnswer() {
        // Used after a new answer arrives — show the start of the answer, not its end.
        const axonRows = document.querySelectorAll('.chat-messages .axon-row');
        if (axonRows.length === 0) return;
        const last = axonRows[axonRows.length - 1];
        last.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    toggleSidebar() {
        this.setState({ sidebarOpen: !this.state.sidebarOpen });
    }

    toggleSources() {
        this.setState({ sourcesOpen: !this.state.sourcesOpen });
    }

    closePanels() {
        this.setState({ sidebarOpen: false, sourcesOpen: false });
    }

    usePrompt(text) {
        this.setState({ chatValue: text }, () => {
            const textarea = document.getElementById('chatInput');
            if (textarea) {
                textarea.focus();
                textarea.style.height = 'auto';
                textarea.style.height = (textarea.scrollHeight) + 'px';
            }
        });
    }

    groupConversationsByDate(conversations) {
        const groups = {
            today: [],
            yesterday: [],
            previous: []
        };

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        conversations.forEach(conv => {
            const date = new Date(conv.created_at);
            if (date >= today) groups.today.push(conv);
            else if (date >= yesterday) groups.yesterday.push(conv);
            else groups.previous.push(conv);
        });

        return groups;
    }

    renderInputArea() {
        const { chatValue, isTyping } = this.state;
        return (
            <div className="chat-input-area">
                <div className="input-row">
                    <div className="chat-input-wrap">
                        <textarea
                            className="chat-textarea"
                            id="chatInput"
                            placeholder="Ask Axon anything..."
                            rows="1"
                            value={chatValue}
                            onChange={this.handleInputChange}
                            onKeyDown={this.handleKey}
                        ></textarea>
                    </div>
                    <button className="send-btn" onClick={this.sendMessage} disabled={!chatValue.trim() || isTyping}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </button>
                </div>

                <div className="input-toolbar">
                    <span className="input-hint-text">Axon AI v1.0 · Neurochecklists Base</span>
                </div>
            </div>
        );
    }

    render() {
        const { messages, hasMessages, isTyping, conversations, references, chatActive, currentConversationId, sidebarOpen, sourcesOpen, error } = this.state;

        const user = this.props.user || {};
        const userName = user.name || 'User';
        const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

        const groupedConversations = this.groupConversationsByDate(conversations);

        return (
            <div className={`page-chat ${chatActive ? 'chat-active' : ''} ${sidebarOpen ? 'sidebar-open' : ''} ${sourcesOpen ? 'sources-open' : ''}`}>
                <Helmet title="Axon — Neurochecklists AI" />

                {(sidebarOpen || sourcesOpen) && <div className="chat-backdrop" onClick={this.closePanels}></div>}

                <aside className="sidebar">
                    <div className="sidebar-header">
                        <Link to="/" className="brand">
                            <AxonIcon size={32} color="var(--blue-500)" />
                            <div>
                                <div style={{ fontSize: '15px', fontWeight: 600, color: '#F9FAFB', letterSpacing: '-0.01em' }}>Neurochecklists</div>
                                <div className="brand-sub">Axon AI</div>
                            </div>
                        </Link>
                        <button className="new-chat-btn" onClick={() => browserHistory.push('/chat')}>
                            <span className="icon">+</span> New conversation
                        </button>
                    </div>
                    <div className="sidebar-body">
                        {['today', 'yesterday', 'previous'].map(group => {
                            if (groupedConversations[group].length === 0) return null;
                            const label = group === 'previous' ? 'Previous' : group.charAt(0).toUpperCase() + group.slice(1);
                            return (
                                <div key={group}>
                                    <div className="chat-group-label">{label}</div>
                                    {groupedConversations[group].map(conv => (
                                        <div key={conv.id} className={`chat-item ${conv.active ? 'active' : ''}`} onClick={() => browserHistory.push(`/chat/${conv.id}`)}>
                                            <span className="chat-item-icon">💬</span>
                                            <span className="chat-item-text">{conv.title || 'New conversation'}</span>
                                            <button className="chat-item-delete" onClick={(e) => this.deleteConversation(conv.id, e)} title="Delete conversation">
                                                <i className="fa fa-trash-o"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    <div className="sidebar-footer">
                        <div className="user-row">
                            <div className="user-avatar">{userInitials}</div>
                            <div className="user-name">{userName}</div>
                            <div className="user-options">⋮</div>
                        </div>
                    </div>
                </aside>

                <main className="chat-main">
                    <div className="chat-topbar chat-topbar-stacked">
                        <div className="topbar-row topbar-row-logo">
                            <button className="hamburger-btn mobile-menu-btn" onClick={this.toggleSidebar} aria-label="Menu">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                            </button>
                            <Link to="/" className="topbar-logo" aria-label="Neurochecklists home">
                                <img src={logo} alt="Neurochecklists" />
                            </Link>
                            <div className="topbar-spacer" aria-hidden="true"></div>
                        </div>
                        <div className="topbar-row topbar-row-tabs">
                            <div className="nav-tabs nav-tabs-prominent">
                                <Link
                                    to="/"
                                    className={`nav-tab ${typeof window !== 'undefined' && window.location.pathname === '/' ? 'active' : ''}`}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                    Search Database
                                </Link>
                                <Link
                                    to="/chat"
                                    className={`nav-tab ${typeof window !== 'undefined' && window.location.pathname.startsWith('/chat') ? 'active' : ''}`}
                                >
                                    <AxonIcon size={18} />
                                    Chat with Axon
                                </Link>
                            </div>
                        </div>
                    </div>

                    {!hasMessages ? (
                        <div className="welcome-screen">
                            <div className="welcome-avatar">
                                <AxonIcon size={56} color="white" />
                            </div>
                            <div className="welcome-title">Hello, I'm Axon</div>
                            <div className="welcome-sub">Your intelligent neurology assistant. Ask me anything — from diagnostic criteria to treatment protocols.</div>

                            <div className="welcome-input-container">
                                {this.renderInputArea()}
                            </div>
                        </div>
                    ) : (
                        <div className="chat-messages">
                            {(() => {
                                const currentConv = conversations.find(c => c.id === currentConversationId);
                                if (!currentConv) return null;
                                const convDate = new Date(currentConv.created_at);
                                const now = new Date();
                                const diffDays = Math.floor((now - convDate) / (1000 * 60 * 60 * 24));
                                let dateLabel = diffDays === 0 ? `Today · ${convDate.toLocaleDateString()}` : diffDays === 1 ? 'Yesterday' : convDate.toLocaleDateString();
                                return <div className="day-divider"><span>{dateLabel}</span></div>;
                            })()}
                            {messages.map((msg, idx) => {
                                if (!msg) return null; // Prevent crash if a message is undefined
                                return (
                                    <div key={idx} className={`msg-row ${msg.role === 'user' ? 'user msg-user' : 'axon-row'}`}>
                                        <div className={`msg-avatar ${msg.role === 'axon' ? 'axon' : 'user-av'}`}>
                                            {msg.role === 'axon' ? (
                                                <AxonIcon size={20} color="white" className="axon-av-icon" />
                                            ) : userInitials}
                                        </div>
                                        <div className="msg-content">
                                            <div className="msg-sender">{msg.role === 'axon' ? 'Axon' : userName}</div>
                                            <div className="bubble" dangerouslySetInnerHTML={{ __html: this.md.render(msg.content || '') }} />
                                            {msg.role === 'axon' && msg.message_references && msg.message_references.length > 0 && (
                                                <div className="citations">
                                                    {msg.message_references.reduce((acc, ref) => {
                                                        if (!acc.some(r => r.file_name === ref.file_name)) acc.push(ref);
                                                        return acc;
                                                    }, []).map((ref, i) => (
                                                        <a key={i} href={`/explore/checklists/${ref.tag || ref.slug || fileNameToSlug(ref.file_name)}`} className="cite-chip">
                                                            <svg className="cite-chip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                                <polyline points="14 2 14 8 20 8"/>
                                                                <line x1="8" y1="13" x2="16" y2="13"/>
                                                                <line x1="8" y1="17" x2="16" y2="17"/>
                                                            </svg>
                                                            {ref.title || ref.file_name}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="msg-time">{msg.time}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {error && <div className="error-row"><div className="error-bubble">{error}</div></div>}
                            {isTyping && <div className="typing-row"><div className="typing-bubble"><div className="typing-dot"></div><div className="typing-dot"></div><div className="typing-dot"></div></div></div>}
                            <div ref={(el) => { this.messagesEnd = el; }}></div>
                        </div>
                    )}
                    {hasMessages && this.renderInputArea()}
                </main>

                <aside className="source-panel">
                    <div className="source-panel-header">
                        <div className="source-panel-title">Sources</div>
                        <div className="source-panel-sub">{references.length} documents referenced</div>
                    </div>
                    <div className="source-body">
                        {references.map((ref, i) => (
                            <div key={i} className="source-card">
                                <div className="source-card-top">
                                    <div className="source-num">{i + 1}</div>
                                    <a className="source-name" href={`/explore/checklists/${ref.tag || ref.slug || fileNameToSlug(ref.file_name)}`}>{ref.title || ref.file_name}</a>
                                </div>
                                <div className="source-desc">{ref.text_snippet.substring(0, 150)}...</div>
                                <div className="source-meta">
                                    <span className="source-tag">
                                        <svg className="source-tag-icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                        Checklist
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        );
    }
}

export default AuthMixin(ChatPage);
